import { useRef, useState } from "react";
import { useStore } from "react-redux";
import { InPortal, OutPortal } from "react-reverse-portal";

import { RootState } from "store/rootReducer";
import { useWellCardPanelStore } from "stores/wellCardPanel";
import styled from "styled-components";

import { MultiPhaseChart } from "components/multiphase-chart/components";
import MPCSpecificSettingsWrapper from "components/multiphase-chart/components/chart/MPCSpecificSettingsWrapper";
import { ChartSettingsProvider as MultiPhaseChartSettingsProvider } from "components/multiphase-chart/context";
import type { IChartSettingsState } from "components/multiphase-chart/context/ChartSettingsContext";
import { useScreenshotContext } from "components/screenshot/hooks";

import {
  WELL_CARD_CHART_STATE,
  WELL_CARD_PANEL_CHART_ID,
  WELL_CARD_PANEL_CHART_TYPE
} from "./constants";
import { useChartSettleResize } from "./hooks/useChartSettleResize";
import { useEmbedScreenshotSheet } from "./hooks/useEmbedScreenshotSheet";
import { usePublishChartNormalizeBy } from "./hooks/usePublishChartNormalizeBy";
import { usePublishChartPreset } from "./hooks/usePublishChartPreset";

const noop = () => undefined;

/** Renders nothing: the hooks have to run inside the chart's settings provider. */
function ChartStoreSync(): null {
  usePublishChartNormalizeBy();
  usePublishChartPreset();
  return null;
}

/**
 * One chart instance serves Summary and Production. It renders at a fixed
 * position in the panel body's tree so a tab switch does not remount it — a
 * remount would refetch and throw away every in-session chart change.
 *
 * The height is explicit because the chart measures its container and the tab
 * body is an auto-height scroll column.
 *
 * `uwi` is the panel's target. Without it the chart would read the map
 * selection and plot a different well from the rest of the card.
 */
export function PanelChart({ uwi }: { uwi: Maybe<string> }): JSX.Element {
  // The same test the chart's own `Screenshot` runs to decide it is the widget
  // being shot. Its screenshot is keyed on the chart's id, so unlike the
  // viewers this cannot be another instance's.
  const { widget } = useScreenshotContext();
  const { node, inSheet } = useEmbedScreenshotSheet(
    "chart",
    widget?.widgetId === WELL_CARD_PANEL_CHART_ID
  );

  // The card's box moves under the chart after the chart has measured it:
  // see `useChartSettleResize`.
  const areaRef = useRef<HTMLDivElement>(null);
  useChartSettleResize(areaRef);

  const store = useStore<RootState>();

  // Read once: the provider takes its seed at mount, and the preset the chart
  // then publishes back must not reseed it. Global normalization is seeded the
  // way a dashboard widget gets it: the toolbar's sync only follows later
  // toggles, so a card opened while it is on would start raw.
  const [initialState] = useState<IChartSettingsState>(() => {
    const preset = useWellCardPanelStore.getState().chartPreset;
    const globalNormalize = store.getState().normalizeBy;
    const state = preset
      ? { ...WELL_CARD_CHART_STATE, currentPreset: preset, storedPreset: preset }
      : WELL_CARD_CHART_STATE;
    return globalNormalize.useNormalizeBy
      ? {
          ...state,
          settings: {
            ...state.settings,
            useNormalizeBy: true,
            normalizeBy: { ...globalNormalize.globalNormalizeBy }
          }
        }
      : state;
  });

  return (
    <ChartArea ref={areaRef} data-testid="well-card-panel-chart">
      <MultiPhaseChartSettingsProvider initialState={initialState}>
        <ChartStoreSync />
        {/* The chart stays in this component's React tree wherever its DOM is
            shown, so the settings provider, the query cache and every
            in-session change survive the move into the sheet and back. */}
        <InPortal node={node}>
          <MultiPhaseChart
            id={WELL_CARD_PANEL_CHART_ID}
            onFullscreenToggle={noop}
            type={WELL_CARD_PANEL_CHART_TYPE}
            uwi={uwi}
            /* The card's chart is not a widget and is saved with nothing, so a
               change to it must not mark the dashboard modified. */
            isDashboardWidget={false}
          />
          <MPCSpecificSettingsWrapper message="Chart-specific settings" />
        </InPortal>
        {!inSheet && <OutPortal node={node} />}
      </MultiPhaseChartSettingsProvider>
    </ChartArea>
  );
}

const ChartArea = styled.div`
  position: relative;
  flex: none;
  height: 380px;
  padding: 0 4px;

  /*
   * The chart's own boxes are a grid item inside a grid item, and neither sets
   * a minimum of its own - so each takes its content's min-content size as its
   * automatic minimum and refuses to shrink past it. Measured on a restore
   * from the dock: the plot held 872px inside an 858px container, the 14px
   * being the scrollbar that appeared after it had measured, and it stayed
   * there. The same rule in the other axis is what leaves a short plot with
   * dead space under it.
   *
   * Scoped to the card's own chart. A dashboard widget sits in a box that does
   * not move under it and is left exactly as it is.
   */
  .chart-wrapper,
  .chart-wrapper > div,
  .ChartPlot,
  .echarts-wrapper {
    min-width: 0;
    min-height: 0;
  }
`;
